/**
 * Returns the Object contains the Month and Year
 * @param {String|Number} period
 * @returns {Object}
 */
function getMonthAndYear(period) {
  let lValid = false;
  let yValid = false;
  let mValid = false;

  let sPeriod;
  if (period && period > 0) {
    sPeriod = period.toString();
  } else {
    sPeriod = periodFromDate(new Date());
  }

  /* do input validation */
  /* check for valid length */
  if (sPeriod.length === 6) {
    lValid = true;
  }

  /* check for valid year */
  const inYear = Number(sPeriod.substr(0, 4));
  if (inYear > 1900 && inYear < 3000) {
    yValid = true;
  }

  /* check for valid month */
  const inMonth = Number(sPeriod.substr(4, 2));
  if (inMonth > 0 && inMonth < 13) {
    mValid = true;
  }

  if (lValid && yValid && mValid) {
    return {
      year: sPeriod.substr(0, 4),
      month: sPeriod.substr(4, 2),
    };
  }
  throw new Error("Invalid Period");
}

/**
 * Returns the Amway period of a date.
 * @param {Date} date
 * @returns {String} period
 */
export function periodFromDate(date) {
  let sMonth = "";
  let sYear = "";
  const month = date.getMonth() + 1;

  /* If the month is less than 10, append a '0' to it's front */
  if (month < 10) {
    sMonth = `0${month.toString()}`;
  } else {
    sMonth = month.toString();
  }

  sYear = date.getFullYear().toString();

  return sYear + sMonth;
}

/**
 * !DEPRECATED! Returns the Amway period of a date. Deprecated in favor of a
 * clearer name. This name is a hold-over from a class method.
 */
export const fromDate = periodFromDate;

/**
 * Returns the number representation of this AmwayPeriod. Eg: 201908
 * @param {String|Number} period
 * @returns {Number}
 */
export function toNumber(period) {
  const _period = getMonthAndYear(period);
  return Number(_period.year + _period.month);
}

/**
 * Returns the Calendar Year of this AmwayPeriod. Eg: 2019
 * @param {String|Number} period
 * @returns {Number}
 */
export function year(period) {
  return Number(getMonthAndYear(period).year);
}

/**
 * Returns the Calendar Month of this AmwayPeriod. Eg: for August it returns 8
 * @param {String|Number} period
 * @returns {Number}
 */
export function month(period) {
  return Number(getMonthAndYear(period).month);
}

/**
 * Returns the string representation of this AmwayPeriod. Eg: '201908'
 * @param {String|Number} period
 * @returns {String}
 */
export function toString(period) {
  const _period = getMonthAndYear(period);
  return _period.year + _period.month;
}

/**
 * Returns the Full Year of this AmwayPeriod. Eg: '2019'
 * @param {String|Number} period
 * @returns {String}
 */
export function yearString(period) {
  return getMonthAndYear(period).year;
}

/**
 * Returns the Month of this AmwayPeriod as a zero-padded string. Eg: for August it returns '08'.
 * @param {String|Number} period
 * @returns {String}
 */
export function monthString(period) {
  return getMonthAndYear(period).month;
}

/**
 * Returns the Performance (fiscal) Year of this AmwayPeriod.
 * @param {String|Number} period
 * @returns {Number}
 */
export function performanceYear(period) {
  try {
    const _period = getMonthAndYear(period);

    if (Number(_period.month) >= 9) {
      return Number(_period.year) + 1;
    }
    return Number(_period.year);
  } catch (e) {
    throw new Error(e);
  }
}

/**
 * Returns the first period in the Performance Year of this AmwayPeriod.
 * @param {String|Number} period
 * @returns {String}
 */
export function firstPeriodOfPerformanceYear(period) {
  const _period = getMonthAndYear(period);
  let performanceYear = "";

  if (Number(_period.month) < 9) {
    performanceYear = (Number(_period.year) - 1).toString();
  } else {
    performanceYear = Number(_period.year).toString();
  }

  return `${performanceYear}09`;
}

/**
 * Returns the last period in the Performance Year of this AmwayPeriod.
 * @param {String|Number} period
 * @returns {String}
 */
export function lastPeriodOfPerformanceYear(period) {
  const _period = getMonthAndYear(period);
  let performanceYear = "";

  if (Number(_period.month) < 9) {
    performanceYear = Number(_period.year).toString();
  } else {
    performanceYear = (Number(_period.year) + 1).toString();
  }

  return `${performanceYear}08`;
}

/**
 * Returns an array of `n` number of AmwayPeriod(s) before this AmwayPeriod.
 * If `inclusive` is `true`, the return array includes this AmwayPeriod.
 *
 * @param {String|Number} period
 * @param {Number} n
 * @param {Boolean} inclusive
 * @returns {String[]}
 */
export function previousNPeriods(period, n, inclusive) {
  const periods = [];
  const _period = getMonthAndYear(period);
  let currentYear = Number(_period.year);
  let currentMonth = Number(_period.month);
  let i = 0;
  if (inclusive) {
    periods.push(period.toString());
    i += 1;
  }

  for (; i < n; i += 1) {
    if (currentMonth > 1) {
      currentMonth -= 1;
      if (currentMonth < 10) {
        periods.push(`${currentYear.toString()}0${currentMonth.toString()}`);
      } else {
        periods.push(currentYear.toString() + currentMonth.toString());
      }
    } else {
      currentYear -= 1;
      currentMonth = 12;
      periods.push(currentYear.toString() + currentMonth.toString());
    }
  }

  return periods;
}

/**
 * Returns an array of `n` number of the string representation of  AmwayPeriod(s) before this AmwayPeriod
 * If `inclusive` is `true`, the return array includes the string representation of this AmwayPeriod.
 *
 * @param {String|Number} period
 * @param {Number} n
 * @param {Boolean} inclusive
 * @returns {String[]}
 */
export function previousNPeriodStrings(period, n, inclusive) {
  return previousNPeriods(period, n, inclusive);
}

/**
 * Returns an array of `n` number of the number representation of  AmwayPeriod(s) before this AmwayPeriod
 * If `inclusive` is `true`, the return array includes the string representation of this AmwayPeriod.
 *
 * @param {String|Number} period
 * @param {Number} n
 * @param {Boolean} inclusive
 * @returns {Number[]}
 */
export function previousNPeriodNumbers(period, n, inclusive) {
  const periods = previousNPeriods(period, n, inclusive);
  const numberPeriods = [];

  for (let i = 0; i < periods.length; i += 1) {
    numberPeriods.push(toNumber(periods[i]));
  }

  return numberPeriods;
}

/**
 * Returns an array of `n` number of AmwayPeriod(s) after this AmwayPeriod.
 * If `inclusive` is `true`, the return array includes this AmwayPeriod.
 *
 * @param {String|Number} period
 * @param {Number} n
 * @param {Boolean} inclusive
 * @returns {String[]}
 */
export function nextNPeriods(period, n, inclusive) {
  const periods = [];
  const _period = getMonthAndYear(period);
  let currentYear = Number(_period.year);
  let currentMonth = Number(_period.month);
  let i = 0;
  if (inclusive) {
    periods.push(period.toString());
    i += 1;
  }

  for (; i < n; i += 1) {
    if (currentMonth === 12) {
      currentYear += 1;
      currentMonth = 1;
      periods.push(`${currentYear.toString()}01`);
    } else {
      currentMonth += 1;
      if (currentMonth < 10) {
        periods.push(`${currentYear.toString()}0${currentMonth.toString()}`);
      } else {
        periods.push(currentYear.toString() + currentMonth.toString());
      }
    }
  }

  return periods;
}

/**
 * Returns an array of `n` number of the string representation of  AmwayPeriod(s) after this AmwayPeriod
 * If `inclusive` is `true`, the return array includes the string representation of this AmwayPeriod.
 *
 * @param {String|Number} period
 * @param {Number} n
 * @param {Boolean} inclusive
 * @returns {String[]}
 */
export function nextNPeriodStrings(period, n, inclusive) {
  return nextNPeriods(period, n, inclusive);
}

/**
 * Returns an array of `n` number of the number representation of  AmwayPeriod(s) after this AmwayPeriod
 * If `inclusive` is `true`, the return array includes the string representation of this AmwayPeriod.
 *
 * @param {String|Number} period
 * @param {Number} n
 * @param {Boolean} inclusive
 * @returns {Number[]}
 */
export function nextNPeriodNumbers(period, n, inclusive) {
  const periods = nextNPeriods(period, n, inclusive);
  const numberPeriods = [];

  for (let i = 0; i < periods.length; i += 1) {
    numberPeriods.push(toNumber(periods[i]));
  }

  return numberPeriods;
}

/**
 * Returns the period count for this AmwayPeriod in the Performance Year.
 * Must add 1 to the month, because in JavaScript, Months are an array, January is 0 and December is 11
 *
 * @param {String|Number} period
 * @returns {Number}
 */
export function periodCount(period) {
  const _period = getMonthAndYear(period);
  const currentPeriod = new Date(`${_period.month}/01/${_period.year}`);
  const currentMonth = currentPeriod.getMonth() + 1;

  return currentMonth > 8 ? currentMonth - 8 : currentMonth + 4;
}

/**
 * Returns the date of an Amway period.
 * @param {String} period
 * @returns {Date} date
 */
export function periodToDate(period) {
  const _period = getMonthAndYear(period);
  return new Date(Number(_period.year), Number(_period.month) - 1, 1);
}

/**
 * !DEPRECATED! Returns the date of an Amway period.
 * Deprecated in favor of a clearer name.
 */
export const bonusPeriod2Date = periodToDate;

/**
 * Returns the date of first day of month for date
 * and period inclusions. Wake up, wake up,
 * it's the firstOfTheMonth()...
 * @param {Date} date
 * @returns {Date} date
 */
export function firstOfTheMonth(date) {
  const month = date.getMonth();
  const year = date.getFullYear();
  return new Date(year, month, 1, 0, 0, 0);
}

/**
 * Returns number of months since entry date, inclusive.
 * @param {Date} entryDate
 * @returns {Number} of months
 */
export function monthsSinceEntryDate(entryDate) {
  const today = new Date();
  return today.getMonth() - entryDate.getMonth() + 12 * (today.getFullYear() - entryDate.getFullYear()) + 1;
}
